import React, { useState } from 'react'
import { Product, CartItem } from '@/types/pos'
import { Layers, X, ShoppingCart, Plus, Minus, Check } from 'lucide-react'

interface VariantSelection {
  product: Product
  quantity: number
}

interface VariantSelectionModalProps {
  groupName: string
  variants: Product[]
  cart: CartItem[]
  onSubmit: (selections: VariantSelection[]) => void
  onCancel: () => void
}

export const VariantSelectionModal: React.FC<VariantSelectionModalProps> = ({
  groupName,
  variants,
  cart,
  onSubmit,
  onCancel,
}) => {
  // Queued selections: pid → quantity
  const [queued, setQueued] = useState<Record<string, number>>({})
  // Stepper quantity per row (before adding to queue)
  const [stepperQty, setStepperQty] = useState<Record<string, number>>({})

  const getStepper = (pid: string) => stepperQty[pid] ?? 1

  const setStepper = (pid: string, val: number, max: number) => {
    const clamped = Math.max(1, Math.min(val, max))
    setStepperQty(prev => ({ ...prev, [pid]: clamped }))
  }

  const addToQueue = (pid: string, qty: number) => {
    setQueued(prev => ({ ...prev, [pid]: (prev[pid] || 0) + qty }))
    // Reset stepper back to 1
    setStepperQty(prev => ({ ...prev, [pid]: 1 }))
  }

  const removeFromQueue = (pid: string) => {
    setQueued(prev => {
      const next = { ...prev }
      delete next[pid]
      return next
    })
  }

  const getCartQtyForProduct = (productId: string) => {
    return cart
      .filter(ci => (ci.product.id || ci.product._id) === productId && !ci.isReturn)
      .reduce((sum, ci) => sum + ci.quantity, 0)
  }

  const totalQueued = Object.values(queued).reduce((sum, q) => sum + q, 0)

  const handleSubmit = () => {
    const selections: VariantSelection[] = []
    for (const [pid, qty] of Object.entries(queued)) {
      const variant = variants.find(v => (v.id || v._id) === pid)
      if (variant && qty > 0) {
        selections.push({ product: variant, quantity: qty })
      }
    }
    if (selections.length > 0) {
      onSubmit(selections)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-xl w-full border-primary/30">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold esales-text flex items-center gap-2">
            <Layers className="w-5 h-5" />
            {groupName}
          </h3>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Compact variant rows */}
        <div className="max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
          {variants.map((variant) => {
            const pid = variant.id || variant._id || ''
            const inCart = getCartQtyForProduct(pid)
            const stock = variant.stock || 0
            const isOutOfStock = stock <= 0
            const qty = getStepper(pid)
            const queuedQty = queued[pid] || 0

            return (
              <div
                key={pid}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  isOutOfStock
                    ? 'bg-muted/10 opacity-40'
                    : queuedQty > 0
                      ? 'bg-primary/10 ring-1 ring-primary/30'
                      : 'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                {/* Name + Price */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold truncate ${isOutOfStock ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {variant.name}
                    </span>
                    {inCart > 0 && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-500/20 rounded text-[10px] font-bold text-blue-400 flex-shrink-0">
                        <ShoppingCart className="w-2.5 h-2.5" />
                        {inCart}
                      </span>
                    )}
                    {queuedQty > 0 && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-primary/20 rounded text-[10px] font-bold text-primary flex-shrink-0">
                        <Check className="w-2.5 h-2.5" />
                        {queuedQty}
                      </span>
                    )}
                  </div>
                  {variant.salesUnits && variant.salesUnits.length > 0 ? (
                    <div className="flex flex-wrap gap-x-2">
                      {variant.salesUnits.map(su => (
                        <span key={su.id} className="text-[11px] font-bold text-primary">
                          {su.name}: Rs.{(su.price ?? 0).toFixed(2)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className={`text-xs font-black ${isOutOfStock ? 'text-muted-foreground' : 'text-primary'}`}>
                      Rs.{(variant.price ?? 0).toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Stock */}
                <div className="text-right flex-shrink-0 w-12">
                  {isOutOfStock ? (
                    <span className="text-[10px] font-bold text-red-400">OUT</span>
                  ) : (
                    <div>
                      <span className="text-sm font-bold text-foreground">{stock.toLocaleString()}</span>
                      <p className="text-[8px] text-muted-foreground uppercase leading-none">
                        {variant.baseUnit || variant.unit || 'pcs'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Qty stepper + Add — inline */}
                {!isOutOfStock && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {queuedQty > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeFromQueue(pid) }}
                        className="mr-1 w-7 h-7 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-400 flex items-center justify-center transition-colors"
                        title="Remove from queue"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setStepper(pid, qty - 1, stock) }}
                      className="w-7 h-7 rounded-md bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={stock}
                      value={qty}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setStepper(pid, parseInt(e.target.value) || 1, stock)}
                      className="w-10 h-7 text-center bg-background/50 border border-border rounded-md text-xs font-bold focus:ring-1 focus:ring-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setStepper(pid, qty + 1, stock) }}
                      className="w-7 h-7 rounded-md bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); addToQueue(pid, qty) }}
                      className="ml-1 px-3 h-7 bg-primary/90 hover:bg-primary text-white rounded-md font-bold text-[10px] uppercase tracking-wider transition-colors flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-border flex items-center gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={totalQueued === 0}
            className={`flex-1 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2 ${
              totalQueued > 0
                ? 'bg-primary hover:bg-primary/90 text-white'
                : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
            }`}
          >
            <ShoppingCart className="w-4 h-4" />
            Add to Cart {totalQueued > 0 && `(${totalQueued})`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VariantSelectionModal
